import React, { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useSearchHSNQuery } from '../api/hsnsac.api';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

const PAGE_SIZE = 25;

type OptionType = { label: string; value: string; disabled?: boolean };

const determineCodeType = (input: string): string => {
  const trimmedInput = input.trim();
  if (trimmedInput.length === 0) return 'HSN';

  if (trimmedInput.match(/^99/)) {
    return 'SAC';
  }

  if (trimmedInput.match(/^[1-8]/)) {
    return 'HSN';
  }
  return 'HSN';
};

export type HsnCodeAutoCompleteProps = {
  value: string | null;
  onChange: (value: OptionType | null) => void;
  error?: boolean;
  helperText?: string;
};

export default function HsnCodeAutoComplete({ value, onChange, error, helperText }: HsnCodeAutoCompleteProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1]));
  const [allData, setAllData] = useState<OptionType[]>([]);

  // Use value for both create and edit scenarios
  const effectiveValue = (value === null || value === undefined || value === 'undefined') ? '' : value;
  const searchWord = inputValue || effectiveValue;
  const codeType = determineCodeType(searchWord);



  const {
    data = [],
    isLoading,
    isFetching,
  } = useSearchHSNQuery(
    {
      rowfrom: (currentPage - 1) * PAGE_SIZE,
      word: searchWord.length >= 3 ? searchWord : (effectiveValue && effectiveValue.length >= 1 ? effectiveValue : ''),
      codetype: codeType,
      limitno: PAGE_SIZE,
    },
    {
      skip: !loadedPages.has(currentPage) || (searchWord.length < 3 && effectiveValue.length >= 1 && allData.length > 0), 
    }
  );

  console.log(data, 'data', allData, 'allData')

  useEffect(() => {
    if (data.length > 0) {
      setAllData(prevData => {
        const newData = [...prevData];
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        
        data.forEach((item, index) => {
          newData[startIndex + index] = item;
        });
        return newData.filter(item => item !== undefined);
      });
    }
  }, [data, currentPage]);

  const options = allData;

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const ul = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = ul;
    
    if (isLoading || isFetching ) return;

    if (scrollTop + clientHeight >= scrollHeight - 2) {
      const nextPage = currentPage + 1;
      if (!loadedPages.has(nextPage)) {
        setLoadedPages(prev => new Set([...prev, nextPage]));
        setCurrentPage(nextPage);
      }
    }
    
    if (scrollTop <= 2 && currentPage > 1) {
      const prevPage = currentPage - 1;
      if (!loadedPages.has(prevPage)) {
        setLoadedPages(prev => new Set([...prev, prevPage]));
        setCurrentPage(prevPage);
      }
    }
  };

  const debounced = useMemo(
    () =>
      debounce((searchText: string) => {
        if (searchText.length >= 3) {
          setCurrentPage(1);
          setLoadedPages(new Set([1]));
          setAllData([]);
          setInputValue(searchText.trim());
        } else {
          setInputValue(searchText.trim());
        }
      }, 500),
    []
  );


  return (
    <Autocomplete
      fullWidth
      size="small"
       open={open}
      onOpen={() => {
        setOpen(true);
        if (effectiveValue && effectiveValue.length >= 1) {
          const isValueInData = allData.find(item => item.value === effectiveValue);
          if (!isValueInData) {
            setCurrentPage(1);
            setLoadedPages(new Set([1]));
            setInputValue(effectiveValue);
          }
        }
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={options}
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        return opt.label || '';
      }}
      value={effectiveValue ? options.find((item) => item.value === effectiveValue && !item.disabled) || null : null}
      onChange={(e, newVal) => {
        if (typeof newVal === 'string') {
          onChange({ label: newVal, value: newVal });
        } else if (newVal && typeof newVal === 'object') {
          onChange(newVal);
        } else {
          onChange(null);
        }
        setOpen(false);
      }}
      filterOptions={(opts) => opts}
      loading={isLoading }
      freeSolo
      onInputChange={(e, newInput, reason) => {
        if (reason === 'input') {
          if (newInput !== 'Please enter at least 3 characters to search') {
            debounced(newInput);
          }
        } else if (reason === 'clear') {
          setInputValue('');
          setCurrentPage(1);
          setLoadedPages(new Set([1]));
          setAllData([]);
        }
      }}
      ListboxProps={{
        onScroll: handleScroll,
        sx: { maxHeight: 300, overflow: 'auto' },
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.value}
          style={{
            ...props.style,
            opacity: option.disabled ? 0.6 : 1,
            cursor: option.disabled ? 'not-allowed' : 'default',
            fontStyle: option.disabled ? 'italic' : 'normal',
          }}
        >
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="HSN/SAC Code"
          placeholder="Select or search HSN/SAC code"
          margin="dense"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(isLoading) && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={!!error}
          helperText={helperText}
          sx={{
            '& .MuiInputBase-input::placeholder': {
              color: 'black',
              opacity: 1,
            },
          }}
        />
      )}
    />
  );
}